import { useState } from "react";
import dayjs from "dayjs";
import { Dialog } from "@headlessui/react";
import { Select, SelectOption } from "@normasteaching/norma-ui";
import { Modal } from "../UI/Modal/Modal.tsx";
import { InputWithLabel } from "../UI/InputWithLabel.tsx";
import { PrimaryButton } from "../UI/PrimaryButton.tsx";
import { ErrorFeedback } from "../UI/Feedback/ErrorFeedback.tsx";
import { Interval } from "../Calendar/Interval.tsx";
import { useExportBookedClassRoomsCsv } from "./useExportBookedClassRoomsCsv.ts";

const dateInputFormat = "YYYY-MM-DD";

type ExportFormProps = {
  defaultInterval: Interval;
  defaultTeacherId: string | null;
  teacherOptions: SelectOption<string>[];
};

function DateField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <InputWithLabel label={label}>
      <input
        type="date"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={"px-3 py-2 border border-gray-300 rounded-md"}
      />
    </InputWithLabel>
  );
}

function ExportBookedClassRoomsCsvForm({
  defaultInterval,
  defaultTeacherId,
  teacherOptions,
  onExported,
}: ExportFormProps & { onExported: () => void }) {
  const [fromDate, setFromDate] = useState(
    defaultInterval.fromDate.format(dateInputFormat),
  );
  const [toDate, setToDate] = useState(
    defaultInterval.toDate.format(dateInputFormat),
  );
  const [teacherId, setTeacherId] = useState<string | null>(defaultTeacherId);
  const [hasExportFailed, setHasExportFailed] = useState(false);
  const { exportBookedClassRoomsCsv, loading } = useExportBookedClassRoomsCsv();

  const isDateRangeValid =
    !!fromDate && !!toDate && !dayjs(toDate).isBefore(dayjs(fromDate));

  async function exportCsv() {
    setHasExportFailed(false);
    try {
      await exportBookedClassRoomsCsv({
        interval: new Interval(
          dayjs(fromDate).startOf("day"),
          dayjs(toDate).endOf("day"),
        ),
        teacherId,
      });
      onExported();
    } catch (error) {
      console.error("Error exporting booked classrooms CSV:", error);
      setHasExportFailed(true);
    }
  }

  return (
    <div className={"space-y-4"}>
      <div className={"grid grid-cols-2 gap-4"}>
        <DateField label={"From"} value={fromDate} onChange={setFromDate} />
        <DateField label={"To"} value={toDate} onChange={setToDate} />
      </div>
      <InputWithLabel label={"Teacher"}>
        <Select
          options={teacherOptions}
          value={teacherId ?? undefined}
          onChange={setTeacherId}
          placeholder={"All teachers"}
          searchable
          clearable
        />
      </InputWithLabel>
      {hasExportFailed && (
        <ErrorFeedback
          title={"Export failed"}
          message={"Unable to export booked classrooms, please try again."}
        />
      )}
      <div className={"flex justify-center pt-2"}>
        <PrimaryButton
          label={"Export CSV"}
          loading={loading}
          disabled={loading || !isDateRangeValid}
          disabledReason={
            isDateRangeValid ? undefined : "End date must follow start date"
          }
          onClick={exportCsv}
        />
      </div>
    </div>
  );
}

export function ExportBookedClassRoomsCsvModal({
  isOpen,
  onClose,
  ...formProps
}: ExportFormProps & {
  isOpen: boolean;
  onClose: () => void;
}) {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={"Export booked classrooms to CSV"}
      overridePanel={({ title, children }) => (
        <Dialog.Panel className="flex-1 md:flex-auto md:max-w-md transform md:rounded-lg bg-white p-6 text-left align-middle shadow-xl transition-all">
          <Dialog.Title
            as="h3"
            className="text-lg font-medium leading-6 text-gray-900"
          >
            {title}
          </Dialog.Title>
          <div className={"mt-4"}>{children}</div>
        </Dialog.Panel>
      )}
    >
      <ExportBookedClassRoomsCsvForm {...formProps} onExported={onClose} />
    </Modal>
  );
}
